import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Title } from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  TablePagination,
  IconButton,
  Chip,
} from "@mui/material";
import { ArrowBack, Search, CheckCircle, Warning } from "@mui/icons-material";
import { StatusChip } from "../../components/StatusChip";
import { CopyableId } from "../../components/CopyableId";
import { API, fetchJson } from "../../api";

interface RoundOverview {
  roundId: string;
  dbBetCount: number;
  fdbBetCount: number;
}

interface Summary {
  roundId: string;
  totalFdbBets: number;
  totalDbBets: number;
  matchCount: number;
  mismatchCount: number;
  missingInFdbCount: number;
  missingInDbCount: number;
  mismatchBreakdown: Record<string, number>;
  walletMismatchCount: number;
}

interface ReconBet {
  betId: string;
  roundId: string;
  userId: string;
  fdbStatus: string | null;
  dbStatus: string | null;
  fdbBetAmount: number | null;
  dbBetAmount: number | null;
  fdbPayoutMultiplier: number | null;
  dbPayoutMultiplier: number | null;
  fdbPayoutAmount: number | null;
  dbPayoutAmount: number | null;
  mismatchFields: string[];
  reconciliationStatus: string;
  expectedWrCount: number;
  actualWrCount: number;
  walletActions: string[];
  walletIssue: string | null;
}

const SummaryCard = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, px: 2, py: 1.5, bgcolor: "#1e293b", borderLeft: "3px solid", borderColor: color }}>
    <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: "0.65rem", color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
      {label}
    </Typography>
  </Box>
);

const rowBg: Record<string, string> = {
  MATCH: "transparent",
  STATUS_MISMATCH: "rgba(248,113,113,0.08)",
  AMOUNT_MISMATCH: "rgba(251,191,36,0.08)",
  MISSING_IN_FDB: "rgba(248,113,113,0.12)",
  MISSING_IN_DB: "rgba(248,113,113,0.12)",
};

// ─── Rounds Overview ───────────────────────────────────────────────

const RoundsOverview = ({ onSelectRound }: { onSelectRound: (roundId: string) => void }) => {
  const [rounds, setRounds] = useState<RoundOverview[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchId, setSearchId] = useState("");
  const location = useLocation();

  const load = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const data = await fetchJson<{ content: RoundOverview[]; totalElements: number }>(`${API}/recon/rounds?page=${p}&size=10`);
      if (data) {
        setRounds(data.content);
        setTotal(data.totalElements);
        setPage(p);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(0);
  }, [load, location.key]);

  return (
    <>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 3 }}>
        <TextField
          placeholder="Jump to Round ID..."
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          size="small"
          sx={{ width: 360 }}
          InputProps={{ sx: { fontFamily: "'JetBrains Mono', monospace" } }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && searchId.trim()) onSelectRound(searchId.trim());
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={() => searchId.trim() && onSelectRound(searchId.trim())}
          disabled={!searchId.trim()}
          startIcon={<Search />}
        >
          Inspect
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 30 }}></TableCell>
                <TableCell>Round ID</TableCell>
                <TableCell align="right">FDB Bets</TableCell>
                <TableCell align="right">DB Bets</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rounds.map((r) => {
                const countMismatch = r.fdbBetCount !== r.dbBetCount;
                return (
                  <TableRow
                    key={r.roundId}
                    hover
                    sx={{ cursor: "pointer", bgcolor: countMismatch ? "rgba(248,113,113,0.04)" : "transparent" }}
                    onClick={() => onSelectRound(r.roundId)}
                  >
                    <TableCell>
                      {countMismatch
                        ? <Warning sx={{ color: "#fbbf24", fontSize: 18 }} />
                        : <CheckCircle sx={{ color: "#34d399", fontSize: 18 }} />}
                    </TableCell>
                    <TableCell><CopyableId value={r.roundId} /></TableCell>
                    <TableCell align="right">{r.fdbBetCount}</TableCell>
                    <TableCell align="right">{r.dbBetCount}</TableCell>
                    <TableCell>
                      {countMismatch ? (
                        <Chip
                          label={`diff: ${Math.abs(r.fdbBetCount - r.dbBetCount)}`}
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem" }}
                        />
                      ) : (
                        <Chip label="OK" size="small" color="success" variant="outlined" sx={{ fontSize: "0.7rem" }} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rounds.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: "#94a3b8" }}>
                    No rounds found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => load(p)}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
          />
        </>
      )}
    </>
  );
};

// ─── Round Detail ──────────────────────────────────────────────────

const RoundDetail = ({ roundId, onBack }: { roundId: string; onBack: () => void }) => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [bets, setBets] = useState<ReconBet[]>([]);
  const [betTotal, setBetTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (p: number) => {
      setLoading(true);
      try {
        const [summaryData, betsData] = await Promise.all([
          fetchJson<Summary>(`${API}/recon/rounds/${roundId}/summary`),
          fetchJson<{ content: ReconBet[]; totalElements: number }>(`${API}/recon/rounds/${roundId}/bets?page=${p}&size=25`),
        ]);
        if (summaryData) setSummary(summaryData);
        if (betsData) {
          setBets(betsData.content);
          setBetTotal(betsData.totalElements);
          setPage(p);
        }
      } finally {
        setLoading(false);
      }
    },
    [roundId],
  );

  useEffect(() => {
    load(0);
  }, [load]);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: "#94a3b8" }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="body2" sx={{ color: "#94a3b8" }}>Back to overview</Typography>
        <Chip label={roundId} size="small" sx={{ fontFamily: "'JetBrains Mono', monospace", ml: 1 }} />
      </Box>

      {loading && !summary ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {summary && (
        <>
          <Grid container spacing={2} columns={14} sx={{ mb: 3 }}>
            <Grid size={{ xs: 7, md: 2 }}>
              <SummaryCard label="FDB Bets" value={summary.totalFdbBets} color="#60a5fa" />
            </Grid>
            <Grid size={{ xs: 7, md: 2 }}>
              <SummaryCard label="DB Bets" value={summary.totalDbBets} color="#60a5fa" />
            </Grid>
            <Grid size={{ xs: 7, md: 2 }}>
              <SummaryCard label="Match" value={summary.matchCount} color="#34d399" />
            </Grid>
            <Grid size={{ xs: 7, md: 2 }}>
              <SummaryCard label="Mismatch" value={summary.mismatchCount} color="#fbbf24" />
            </Grid>
            <Grid size={{ xs: 7, md: 2 }}>
              <SummaryCard label="Missing FDB" value={summary.missingInFdbCount} color="#f87171" />
            </Grid>
            <Grid size={{ xs: 7, md: 2 }}>
              <SummaryCard label="Missing DB" value={summary.missingInDbCount} color="#f87171" />
            </Grid>
            <Grid size={{ xs: 7, md: 2 }}>
              <SummaryCard label="Wallet Issues" value={summary.walletMismatchCount} color={summary.walletMismatchCount > 0 ? "#c084fc" : "#34d399"} />
            </Grid>
          </Grid>

          {Object.keys(summary.mismatchBreakdown).length > 0 && (
            <Card sx={{ mb: 2, bgcolor: "#1e293b", borderLeft: "3px solid #7c3aed" }}>
              <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
                <Typography sx={{ color: "#64748b", fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
                  Mismatch breakdown
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
                  {Object.entries(summary.mismatchBreakdown).map(([field, count]) => (
                    <Typography key={field} variant="body2" sx={{ color: "#fbbf24" }}>
                      {field}: <b>{count}</b>
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {bets.length > 0 && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Bet ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>FDB Status</TableCell>
                <TableCell>DB Status</TableCell>
                <TableCell align="right">FDB Bet</TableCell>
                <TableCell align="right">DB Bet</TableCell>
                <TableCell align="right">FDB Payout x</TableCell>
                <TableCell align="right">DB Payout x</TableCell>
                <TableCell>Mismatches</TableCell>
                <TableCell>Wallet</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bets.map((b) => {
                const hasWalletIssue = !!b.walletIssue;
                const bg = hasWalletIssue && b.reconciliationStatus === "MATCH"
                  ? "rgba(192,132,252,0.08)"
                  : (rowBg[b.reconciliationStatus] ?? "transparent");
                return (
                  <TableRow key={b.betId} sx={{ bgcolor: bg }}>
                    <TableCell>
                      <StatusChip value={b.reconciliationStatus} />
                    </TableCell>
                    <TableCell><CopyableId value={b.betId} /></TableCell>
                    <TableCell><CopyableId value={b.userId} /></TableCell>
                    <TableCell>
                      <StatusChip value={b.fdbStatus} />
                    </TableCell>
                    <TableCell>
                      <StatusChip value={b.dbStatus} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {b.fdbBetAmount ?? "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {b.dbBetAmount ?? "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {b.fdbPayoutMultiplier ?? "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {b.dbPayoutMultiplier ?? "—"}
                    </TableCell>
                    <TableCell>
                      {b.mismatchFields.map((f) => (
                        <Typography
                          key={f}
                          component="span"
                          sx={{ color: "#fbbf24", fontSize: "0.7rem", mr: 1, fontFamily: "'JetBrains Mono', monospace" }}
                        >
                          {f}
                        </Typography>
                      ))}
                    </TableCell>
                    <TableCell>
                      {b.expectedWrCount < 0 ? (
                        <Typography component="span" sx={{ color: "#94a3b8", fontSize: "0.7rem" }}>—</Typography>
                      ) : hasWalletIssue ? (
                        <Chip
                          label={`${b.actualWrCount}/${b.expectedWrCount} [${b.walletActions.join(",")}]`}
                          size="small"
                          sx={{ fontSize: "0.65rem", bgcolor: "rgba(192,132,252,0.2)", color: "#c084fc" }}
                        />
                      ) : (
                        <Chip
                          label={`${b.actualWrCount}/${b.expectedWrCount} OK`}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: "0.65rem" }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={betTotal}
            page={page}
            onPageChange={(_, p) => load(p)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        </>
      )}

      {!loading && bets.length === 0 && summary && summary.totalFdbBets === 0 && summary.totalDbBets === 0 && (
        <Typography variant="body2" sx={{ color: "#94a3b8", mt: 2 }}>
          No bets found in either FDB or DB for this round.
        </Typography>
      )}
    </>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────

export const ReconPage = () => {
  const [selectedRound, setSelectedRound] = useState<string | null>(null);

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Reconciliation" />
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#e2e8f0", letterSpacing: -0.3, mb: 2 }}>
        FDB vs DB Reconciliation
      </Typography>

      {selectedRound ? (
        <RoundDetail roundId={selectedRound} onBack={() => setSelectedRound(null)} />
      ) : (
        <RoundsOverview onSelectRound={setSelectedRound} />
      )}
    </Box>
  );
};
